"use client";
import { useState, useMemo, ChangeEvent, FormEvent, useEffect } from 'react';

type ServiceType = 'recruiting' | 'temp' | 'outsourcing' | 'outstaff' | 'hrsub';
type RecruitingTier = '20' | '25' | '30';
type BillingMode = "monthly" | "annual";
type PkgId = "basic" | "standard" | "premium";

const serviceOptions = [
  { value: 'recruiting', label: 'Подбор персонала — 20%' },
  { value: 'temp', label: 'Временный персонал — 15%' },
  { value: 'outsourcing', label: 'Аутсорсинг — 25%' },
  { value: 'outstaff', label: 'Аутстаффинг — 15%' },
  { value: 'hrsub', label: 'HR-сопровождение (подписка)' },
];

const hrPrices = {
  lite: { month: 39000, year: 32370 },
  std: { month: 69000, year: 57270 },
  max: { month: 109000, year: 90470 },
};

const hrPlanNames = {
  lite: 'Стандарт',
  std: 'Продвинутый',
  max: 'Премиум',
};

const formatCurrency = (n: number): string =>
  new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(n);

const fmt = (n: number) => n.toLocaleString("ru-RU");

const packages = [
  { id: "basic", name: "Базовый", monthlyNet: 39000, note: "Базовые HR‑процессы, KPI и отчётность" },
  { id: "standard", name: "Стандарт", monthlyNet: 69000, note: "Расширенные метрики, аудит и обучение" },
  { id: "premium", name: "Премиум", monthlyNet: 109000, note: "Полный SLA, L&D, автоматизация, Analytics" },
];

function computeHRPlanDetailed(args: {
  monthlyNet: number;
  discountPct?: number;
  vatEnabled?: boolean;
  vatRatePct?: number;
  billingMode?: BillingMode;
}) {
  const monthlyNet = Math.max(0, Number(args.monthlyNet) || 0);
  const discountPct = Math.max(0, Number(args.discountPct ?? 17));
  const vatEnabled = Boolean(args.vatEnabled);
  const vatRatePct = Math.max(0, Number(args.vatRatePct ?? 20));
  const billingMode: BillingMode = args.billingMode ?? "monthly";

  const monthlyGross = vatEnabled ? monthlyNet * (1 + vatRatePct / 100) : monthlyNet;
  const annualNominalGross = monthlyGross * 12;
  const annualDiscountedGross = billingMode === "annual" ? annualNominalGross * (1 - discountPct / 100) : annualNominalGross;
  const savingsGross = annualNominalGross - annualDiscountedGross;
  const effectiveMonthlyGross = annualDiscountedGross / 12;

  return {
    monthlyGross,
    annualNominalGross,
    annualDiscountedGross,
    savingsGross,
    effectiveMonthlyGross,
    discountPct,
    vatEnabled,
    vatRatePct,
  };
}

const BILLING_EVENT = "interim:pricing.billing";
function emitBillingMode(mode: BillingMode) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(BILLING_EVENT, { detail: mode }));
}
function useBillingModeSync(onMode: (m: BillingMode) => void) {
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<BillingMode>;
      onMode(ce.detail === "annual" ? "annual" : "monthly");
    };
    window.addEventListener(BILLING_EVENT, handler as EventListener);
    return () => window.removeEventListener(BILLING_EVENT, handler as EventListener);
  }, [onMode]);
}

function HRAnnualPlanCalculator() {
  const [pkgId, setPkgId] = useState<PkgId>("standard");
  const activePkg = packages.find(p => p.id === pkgId)!;

  const [monthlyNet, setMonthlyNet] = useState<number>(activePkg.monthlyNet);
  useEffect(() => setMonthlyNet(activePkg.monthlyNet), [activePkg.monthlyNet]);

  const [vatEnabled, setVatEnabled] = useState<boolean>(false);
  const [vatRatePct, setVatRatePct] = useState<number>(20);
  const [billingMode, setBillingMode] = useState<BillingMode>("monthly");
  useBillingModeSync(setBillingMode);

  const r = useMemo(() => computeHRPlanDetailed({
    monthlyNet,
    discountPct: 17,
    vatEnabled,
    vatRatePct,
    billingMode,
  }), [monthlyNet, vatEnabled, vatRatePct, billingMode]);

  return (
    <div className="w-full mt-12 bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6 space-y-6">
      <header className="space-y-2">
        <h3 className="text-2xl font-bold text-white">Калькулятор HR‑сопровождения</h3>
        <p className="text-sm text-blue-200">
          Скидка <strong>−17%</strong> действует только при годовой оплате. НДС настраивается.
        </p>
      </header>

      <section className="grid md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-200">Пакет</label>
          <select
            className="w-full rounded-xl border border-white/30 bg-white/10 text-white p-3"
            value={pkgId}
            onChange={(e) => setPkgId(e.target.value as PkgId)}
          >
            {packages.map(p => (
              <option
                key={p.id}
                value={p.id}
                className="bg-gray-800 text-white"
              >
                {p.name} — {fmt(p.monthlyNet)} ₽/мес (нетто)
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-300">{activePkg.note}</p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-200">Цена (нетто), ₽/мес</label>
          <input
            type="number"
            className="w-full rounded-xl border border-white/30 bg-white/10 text-white p-3"
            value={monthlyNet}
            onChange={(e) => setMonthlyNet(Math.max(0, Number(e.target.value)))}
          />
          <p className="text-xs text-gray-400">Можно скорректировать под КП</p>
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-6 items-end">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <input
              id="vat-calc"
              type="checkbox"
              className="h-5 w-5 rounded accent-yellow-400"
              checked={vatEnabled}
              onChange={(e) => setVatEnabled(e.target.checked)}
            />
            <label htmlFor="vat-calc" className="text-sm text-gray-200">С НДС</label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-200">Ставка НДС, %</label>
            <input
              type="number"
              className="w-full rounded-xl border border-white/30 bg-white/10 text-white p-3"
              value={vatRatePct}
              min={0}
              max={50}
              step={0.5}
              onChange={(e) => setVatRatePct(Math.max(0, Math.min(50, Number(e.target.value))))}
            />
          </div>
        </div>

        <div className="flex justify-end items-center gap-3 flex-wrap sm:flex-nowrap w-full overflow-x-auto sm:overflow-visible">
          <span className="text-sm text-gray-300 whitespace-nowrap">Оплата:</span>

          <div className="text-white rounded-2xl p-2 flex items-center gap-2 border border-white/20 min-w-fit">
            <button
              type="button"
              className={`px-3 py-1.5 rounded-2xl transition-all
        ${billingMode === "monthly"
                  ? "bg-[#FFB347]"
                  : `
              bg-orange-400/20 
              sm:bg-transparent 
              sm:hover:bg-orange-400/30 
              active:bg-orange-400/40 
              focus:bg-orange-400/40
            `
                }`}
              onClick={() => {
                setBillingMode("monthly");
                emitBillingMode("monthly");
              }}
            >
              Помесячно
            </button>

            <div className="w-10 h-6 bg-[#E6950080] rounded-full relative mx-1 shrink-0">
              <span
                className="absolute top-0.5 h-5 w-5 bg-white rounded-full transition-all"
                style={{
                  left: billingMode === "annual" ? undefined : "0.5rem",
                  right: billingMode === "annual" ? "0.5rem" : undefined,
                }}
              />
            </div>

            <button
              type="button"
              className={`px-3 py-1.5 rounded-2xl transition-all
        ${billingMode === "annual"
                  ? "bg-[#FFB347]"
                  : `
              bg-orange-400/20
              sm:bg-transparent
              sm:hover:bg-orange-400/30
              active:bg-orange-400/40
              focus:bg-orange-400/40
            `
                }`}
              onClick={() => {
                setBillingMode("annual");
                emitBillingMode("annual");
              }}
            >
              Год −17%
            </button>
          </div>
        </div>

      </section>

      <section className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-white/10 p-5 space-y-3">
          <div className="text-sm font-semibold text-gray-200">Текущая конфигурация</div>
          <div className="text-sm text-gray-300">Пакет: <span className="font-medium">{activePkg.name}</span></div>
          <div className="text-sm text-gray-300">НДС: <span className="font-medium">{r.vatEnabled ? `включён (${r.vatRatePct}%)` : "без НДС"}</span></div>
          <div className="rounded-xl bg-white/10 p-4">
            <div className="text-xs text-gray-400">Месячно (брутто)</div>
            <div className="text-2xl font-bold text-white">{fmt(Math.round(r.monthlyGross))} ₽</div>
          </div>
        </div>

        <div className="rounded-2xl bg-yellow-400/10 p-5 space-y-3">
          <div className="text-sm font-semibold text-yellow-300">Итог</div>
          {billingMode === "monthly" ? (
            <>
              <div className="text-xs text-gray-300">За год (брутто)</div>
              <div className="text-2xl font-bold text-white">{fmt(Math.round(r.annualNominalGross))} ₽</div>
              <div className="text-xs text-amber-300">Скидка −17% не применяется</div>
            </>
          ) : (
            <>
              <div className="text-xs text-gray-300">Годовой план со скидкой −17% (брутто)</div>
              <div className="text-2xl font-bold text-white">{fmt(Math.round(r.annualDiscountedGross))} ₽</div>
              <div className="text-sm text-green-300">Экономия: {fmt(Math.round(r.savingsGross))} ₽</div>
              <div className="text-xs text-gray-400">≈ {fmt(Math.round(r.effectiveMonthlyGross))} ₽/мес</div>
            </>
          )}
        </div>
      </section>

      <section className="rounded-2xl bg-white/5 p-5 text-sm text-gray-300">
        <div className="font-semibold mb-2">Примечания</div>
        <ul className="list-disc pl-5 space-y-1">
          <li>Скидка −17% только при оплате за год.</li>
          <li>НДС настраивается под ваш расчёт, если нужно.</li>
          <li>Финальные условия — в коммерческом предложении.</li>
        </ul>
      </section>
    </div>
  );
}

export default function Pricing() {
  const [billingCycle, setBillingCycle] = useState(false);
  const [serviceType, setServiceType] = useState<ServiceType>('recruiting');
  const [recruitingTier, setRecruitingTier] = useState<RecruitingTier>('20');
  const [calcResult, setCalcResult] = useState('Заполните форму, чтобы увидеть расчёт.');

  const [formData, setFormData] = useState({
    annual: '1200000',
    cnt: '5',
    rate: '350',
    hours: '168',
    base: '45000',
    plan: 'std' as 'lite' | 'std' | 'max',
  });

  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    consent: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | 'consent-error' | null>(null);

  useEffect(() => {
    emitBillingMode(billingCycle ? "annual" : "monthly");
  }, [billingCycle]);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleContactChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setContactForm({
      ...contactForm,
      [name]: type === 'checkbox' ? checked : value,
    });
    setSubmitStatus(null);
  };

  const handleContactSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitStatus(null);

    if (!contactForm.consent) {
      setSubmitStatus('consent-error');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/contact-requests`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: {
              ...contactForm,
              formName: 'Цены', 
            },
          }),
        }
      );

      if (response.ok) {
        setSubmitStatus('success');
        setContactForm({ name: '', phone: '', email: '', message: '', consent: false });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Ошибка при отправке:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleCalcSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let result = '';
    const data = {
      annual: Number(formData.annual) || 0,
      cnt: Number(formData.cnt) || 0,
      rate: Number(formData.rate) || 0,
      hours: Number(formData.hours) || 0,
      base: Number(formData.base) || 0,
      plan: formData.plan,
    };

    switch (serviceType) {
      case 'recruiting': {
        const pct = Number(recruitingTier) / 100;
        const fee = data.annual * pct;
        const tierName = recruitingTier === '20' ? 'Стандартный подбор'
          : recruitingTier === '25' ? 'Сложная роль'
            : 'Целевой поиск';
        result = `Комиссия (${tierName}, ${recruitingTier}%): ${formatCurrency(fee)} единовременно\n(при годовом доходе ${formatCurrency(data.annual)}).`;
        break;
      }
      case 'temp': {
        const baseTemp = data.cnt * data.rate * data.hours;
        const markup = baseTemp * 0.15;
        result = `Месячный бюджет:\nбаза ${formatCurrency(baseTemp)} + наценка 15% = ${formatCurrency(baseTemp + markup)}.`;
        break;
      }
      case 'outsourcing': {
        const baseOut = data.cnt * data.base;
        const ops = baseOut * 0.25;
        result = `Аутсорсинг:\nфикс ${formatCurrency(baseOut)} + операционный пакет 25% = ${formatCurrency(baseOut + ops)}/мес.`;
        break;
      }
      case 'outstaff': {
        const baseStaff = data.cnt * data.base;
        const admin = baseStaff * 0.15;
        result = `Аутстаффинг:\nоклады ${formatCurrency(baseStaff)} + администрирование 15% = ${formatCurrency(baseStaff + admin)}/мес.`;
        break;
      }
      case 'hrsub': {
        const price = billingCycle ? hrPrices[data.plan].year : hrPrices[data.plan].month;
        const planName = hrPlanNames[data.plan];
        result = `Подписка «${planName}»:\n${formatCurrency(price)}/мес ${billingCycle ? '(оплата за год, -17%)' : ''}.`;
        break;
      }
    }
    setCalcResult(result);
  };

  const renderLayout = useMemo(() => {
    switch (serviceType) {
      case 'recruiting':
        return (
          <>
            <div>
              <label htmlFor="annual" className="block text-sm font-medium text-gray-200 mb-1">
                Годовой оклад кандидата, ₽
              </label>
              <input
                type="number"
                id="annual"
                min="120000"
                className="w-full rounded-md px-3 py-2 text-gray-900"
                placeholder="напр., 1 200 000"
                value={formData.annual}
                onChange={handleInputChange}
              />
            </div>
            <div>
              <label htmlFor="recruitingTier" className="block text-sm font-medium text-gray-200 mb-1">
                Сложность роли
              </label>
              <select
                id="recruitingTier"
                className="w-full rounded-md px-3 py-2 text-gray-900"
                value={recruitingTier}
                onChange={(e) => setRecruitingTier(e.target.value as RecruitingTier)}
              >
                <option value="20">Стандартная (20%)</option>
                <option value="25">Сложная роль (25%)</option>
                <option value="30">Целевой поиск (30%)</option>
              </select>
            </div>
          </>
        );
      case 'temp':
        return (
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="cnt" className="block text-sm font-medium text-gray-200 mb-1">Сотрудников</label>
              <input type="number" id="cnt" min="1" className="w-full rounded-md px-3 py-2 text-gray-900" placeholder="5" value={formData.cnt} onChange={handleInputChange} />
            </div>
            <div>
              <label htmlFor="rate" className="block text-sm font-medium text-gray-200 mb-1">Ставка, ₽/час</label>
              <input type="number" id="rate" min="150" className="w-full rounded-md px-3 py-2 text-gray-900" placeholder="350" value={formData.rate} onChange={handleInputChange} />
            </div>
            <div>
              <label htmlFor="hours" className="block text-sm font-medium text-gray-200 mb-1">Часов в мес.</label>
              <input type="number" id="hours" min="1" className="w-full rounded-md px-3 py-2 text-gray-900" placeholder="168" value={formData.hours} onChange={handleInputChange} />
            </div>
          </div>
        );
      case 'outsourcing':
        return (
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="cnt" className="block text-sm font-medium text-gray-200 mb-1">Сотрудников</label>
              <input type="number" id="cnt" min="1" className="w-full rounded-md px-3 py-2 text-gray-900" placeholder="8" value={formData.cnt} onChange={handleInputChange} />
            </div>
            <div>
              <label htmlFor="base" className="block text-sm font-medium text-gray-200 mb-1">Фикс за сотрудника, ₽/мес</label>
              <input type="number" id="base" min="10000" className="w-full rounded-md px-3 py-2 text-gray-900" placeholder="45 000" value={formData.base} onChange={handleInputChange} />
            </div>
          </div>
        );
      case 'outstaff':
        return (
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="cnt" className="block text-sm font-medium text-gray-200 mb-1">Сотрудников</label>
              <input type="number" id="cnt" min="1" className="w-full rounded-md px-3 py-2 text-gray-900" placeholder="12" value={formData.cnt} onChange={handleInputChange} />
            </div>
            <div>
              <label htmlFor="base" className="block text-sm font-medium text-gray-200 mb-1">Оклад, ₽/мес</label>
              <input type="number" id="base" min="20000" className="w-full rounded-md px-3 py-2 text-gray-900" placeholder="60 000" value={formData.base} onChange={handleInputChange} />
            </div>
          </div>
        );
      case 'hrsub':
        return (
          <div>
            <label htmlFor="plan" className="block text-sm font-medium text-gray-200 mb-1">Пакет</label>
            <select id="plan" className="w-full rounded-md px-3 py-2 text-gray-900" value={formData.plan} onChange={handleInputChange}>
              <option value="lite">Стандарт — 39 000 ₽/мес</option>
              <option value="std">Продвинутый— 69 000 ₽/мес</option>
              <option value="max">Премиум— 109 000 ₽/мес</option>
            </select>
          </div>
        );
      default:
        return null;
    }
  }, [serviceType, formData, recruitingTier]);

  const navItems = [
    { id: 'hrsub', label: 'HR-сопровождение' },
    { id: 'recruiting', label: 'Подбор' },
    { id: 'temp', label: 'Временный персонал' },
    { id: 'outsourcing', label: 'Аутсорсинг' },
    { id: 'outstaff', label: 'Аутстаффинг' },
    { id: 'calc', label: 'Калькулятор' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <>
      <style jsx global>{`
        html { scroll-behavior: smooth; }
        [id] { scroll-margin-top: 90px; }
        @media (max-width: 767px) { [id] { scroll-margin-top: 75px; } }
      `}</style>
      <div className="bg-gradient-to-br from-[#003087] to-blue-800 text-gray-100 antialiased">
        <header className="relative px-6 pt-32 pb-24 md:pt-40 md:pb-32 text-center mt-12"
          style={{ background: `linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)` }}>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-4">Цены INTERIM</h1>
          <p className="max-w-3xl mx-auto text-lg md:text-xl text-gray-200">
            Прозрачные модели: фиксированная комиссия за подбор, почасовая ставка с наценкой для временного персонала,
            фикс/персон за аутсорсинг и аутстаффинг, а также подписка на HR-сопровождение.
          </p>
          <div className="mt-8 flex items-center justify-center gap-4">
            <span className="text-sm">Помесячно</span>
            <button
              onClick={() => setBillingCycle(prev => !prev)}
              className="w-12 h-6 bg-white/20 rounded-full flex items-center p-1 transition"
            >
              <span className={`w-4 h-4 bg-white rounded-full transition ${billingCycle ? 'translate-x-6' : ''}`} />
            </button>
            <span className="text-sm">За год <span className="ml-1 text-green-300 text-xs">-17%</span></span>
          </div>
          <nav className="mt-8 flex flex-wrap items-center justify-center gap-3 text-sm">
            {navItems.map(({ id, label }) => (
              <button key={id} onClick={() => scrollToSection(id)} className="chip px-3 py-1 rounded-full hover:bg-white/25 transition bg-white/10">
                {label}
              </button>
            ))}
          </nav>
        </header>

        <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">

          <section id="hrsub">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">HR-сопровождение (подписка)</h2>
            <div className="grid gap-6 md:grid-cols-3 mb-12">
              {Object.entries(hrPrices).map(([plan, prices]) => (
                <div key={plan} className={`card rounded-2xl p-8 flex flex-col bg-white/5 backdrop-blur ${plan === 'std' ? 'border border-[#FFFFFF]/40' : ''}`}>
                  {plan === 'std' && <div className="text-xs uppercase tracking-wider text-yellow-300 mb-1">Рекомендуем</div>}
                  <div className="text-sm text-gray-300 mb-2">Пакет {hrPlanNames[plan as keyof typeof hrPlanNames]}</div>
                  <div className="text-3xl font-extrabold mb-1">{formatCurrency(billingCycle ? prices.year : prices.month)}</div>
                  <p className="text-gray-300 text-sm mb-4">
                    {plan === 'lite' && 'Базовая поддержка малого бизнеса.'}
                    {plan === 'std' && 'Расширенная поддержка и закрытие задач.'}
                    {plan === 'max' && 'Полный HR-контур с SLA.'}
                  </p>
                  <ul className="space-y-2 text-sm mb-4 flex-1">
                    {plan === 'lite' && <><li>• 1× HR-менеджер (до 20 чел.)</li><li>• Консалтинг, шаблоны</li><li>• 1× мини-подбор/мес</li></>}
                    {plan === 'std' && <><li>• 1× HR + 0.5× рекрутер</li><li>• До 3 закрытий/мес</li><li>• KPI и отчётность</li></>}
                    {plan === 'max' && <><li>• HR-лид + рекрутер + аналитика</li><li>• До 6 закрытий/мес</li><li>• SLA с ответственностью</li></>}
                  </ul>
                  <button onClick={() => scrollToSection('contact')} className="mt-auto w-fit btn-accent px-5 py-2 rounded-lg font-semibold bg-yellow-400 text-blue-900 hover:bg-yellow-300 transition">
                    Оформить
                  </button>
                </div>
              ))}
            </div>

            <HRAnnualPlanCalculator />
          </section>

          <section id="recruiting">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Подбор персонала</h2>
            <div className="grid gap-6 md:grid-cols-3">
              <div className="card rounded-2xl p-8 bg-white/5 backdrop-blur">
                <div className="text-sm text-gray-300 mb-2">Комиссия</div>
                <div className="text-3xl font-extrabold mb-1">20% <span className="text-base font-medium">от годового дохода</span></div>
                <p className="text-gray-300 text-sm mb-4">Оплачивается по факту выхода. Гарантия замены — до 60 дней.</p>
                <ul className="space-y-2 text-sm"><li>• Поиск и отбор</li><li>• Интервью и оффер</li><li>• Проверки (по запросу)</li></ul>
                <button onClick={() => scrollToSection('contact')} className="mt-6 inline-block btn-accent px-5 py-2 rounded-lg font-semibold bg-yellow-400 text-blue-900 hover:bg-yellow-300 transition">Оставить заявку</button>
              </div>
              <div className="card rounded-2xl p-8 bg-white/5 backdrop-blur">
                <div className="text-sm text-gray-300 mb-2">Сложные роли</div>
                <div className="text-3xl font-extrabold mb-1">25%</div>
                <p className="text-gray-300 text-sm mb-4">Редкие специалисты / руководители. Гарантия до 90 дней.</p>
                <ul className="space-y-2 text-sm"><li>• Target-поиск</li><li>• Ассессмент</li><li>• Полное сопровождение</li></ul>
                <button onClick={() => scrollToSection('contact')} className="mt-6 inline-block btn-accent px-5 py-2 rounded-lg font-semibold bg-yellow-400 text-blue-900 hover:bg-yellow-300 transition">Запросить расчёт</button>
              </div>
              <div className="card rounded-2xl p-8 bg-white/5 backdrop-blur">
                <div className="text-sm text-gray-300 mb-2">Целевой поиск</div>
                <div className="text-3xl font-extrabold mb-1">30%</div>
                <p className="text-gray-300 text-sm mb-4">Рассрочка по этапам. Максимальная вовлечённость.</p>
                <ul className="space-y-2 text-sm"><li>• Спринт-план</li><li>• Еженедельные отчёты</li><li>• Гарантия до 120 дней</li></ul>
                <button onClick={() => scrollToSection('contact')} className="mt-6 inline-block btn-accent px-5 py-2 rounded-lg font-semibold bg-yellow-400 text-blue-900 hover:bg-yellow-300 transition">Связаться</button>
              </div>
            </div>
          </section>

          <section id="temp">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Временный персонал</h2>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="card rounded-2xl p-8 bg-white/5 backdrop-blur">
                <div className="text-sm text-gray-300 mb-2">Модель расчёта</div>
                <div className="text-3xl font-extrabold mb-1">Ставка + 15% <span className="text-base font-medium">наценка</span></div>
                <p className="text-gray-300 text-sm mb-4">Минимальная смена 4 часа. Оплата по факту.</p>
                <ul className="space-y-2 text-sm"><li>• Гибкое масштабирование</li><li>• Замена без доплат</li><li>• Табели в кабинете</li></ul>
                <button onClick={() => scrollToSection('calc')} className="mt-6 inline-block btn-accent px-5 py-2 rounded-lg font-semibold bg-yellow-400 text-blue-900 hover:bg-yellow-300 transition">Рассчитать бюджет</button>
              </div>
              <div className="card rounded-2xl p-8 bg-white/5 backdrop-blur">
                <div className="text-sm text-gray-300 mb-2">SLA</div>
                <div className="text-3xl font-extrabold mb-1">Фиксация качества</div>
                <p className="text-gray-300 text-sm mb-4">KPI, штрафы, бонусы в договоре.</p>
                <ul className="space-y-2 text-sm"><li>• Онлайн-доступ к метрикам</li><li>• Еженедельные сверки</li></ul>
              </div>
            </div>
          </section>

          <section id="outsourcing">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Аутсорсинг функций</h2>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="card rounded-2xl p-8 bg-white/5 backdrop-blur">
                <div className="text-sm text-gray-300 mb-2">Модель расчёта</div>
                <div className="text-3xl font-extrabold mb-1">Фикс/сотр. + 25% <span className="text-base font-medium">оперпакет</span></div>
                <p className="text-gray-300 text-sm mb-4">Индивидуально под нагрузку и графики.</p>
                <ul className="space-y-2 text-sm"><li>• Регламенты и обучение</li><li>• Замещение и резерв</li><li>• Контроль качества</li></ul>
                <button onClick={() => scrollToSection('contact')} className="mt-6 inline-block btn-accent px-5 py-2 rounded-lg font-semibold bg-yellow-400 text-blue-900 hover:bg-yellow-300 transition">Запросить КП</button>
              </div>
              <div className="card rounded-2xl p-8 bg-white/5 backdrop-blur">
                <div className="text-sm text-gray-300 mb-2">Типовые направления</div>
                <ul className="space-y-2 text-sm"><li>• Call-центр, документооборот</li><li>• Склад, курьеры</li><li>• Ресепшн</li></ul>
              </div>
            </div>
          </section>

          <section id="outstaff">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Аутстаффинг</h2>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="card rounded-2xl p-8 bg-white/5 backdrop-blur">
                <div className="text-sm text-gray-300 mb-2">Модель расчёта</div>
                <div className="text-3xl font-extrabold mb-1">Оклад + 15% <span className="text-base font-medium">администрирование</span></div>
                <p className="text-gray-300 text-sm mb-4">Оформление, расчёт выплат, отчётность.</p>
                <ul className="space-y-2 text-sm"><li>• Снижение рисков</li><li>• Единая точка контакта</li></ul>
              </div>
              <div className="card rounded-2xl p-8 bg-white/5 backdrop-blur">
                <div className="text-sm text-gray-300 mb-2">Гарантии</div>
                <ul className="space-y-2 text-sm"><li>• Соответствие ТК РФ</li><li>• Замена без доплат</li><li>• Аудиты в сопровождении</li></ul>
              </div>
            </div>
          </section>

          <section id="calc">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Калькулятор бюджета</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="simple-card bg-white/5 backdrop-blur p-6 rounded-2xl">
                <form onSubmit={handleCalcSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="serviceType" className="block text-sm font-medium text-gray-200 mb-1">
                      Тип услуги
                    </label>
                    <select
                      id="serviceType"
                      className="w-full rounded-md px-3 py-2 text-gray-900"
                      value={serviceType}
                      onChange={(e) => setServiceType(e.target.value as ServiceType)}
                    >
                      {serviceOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div id="fieldGroup">{renderLayout}</div>
                  <button type="submit" className="w-full btn-accent py-2 rounded-lg font-semibold bg-yellow-400 text-blue-900 hover:bg-yellow-300 transition">
                    Рассчитать
                  </button>
                </form>
              </div>
              <div className="simple-card bg-white/5 backdrop-blur p-6 rounded-2xl">
                <h3 className="text-xl font-semibold mb-2">Результат</h3>
                <div id="result" className="text-lg whitespace-pre-line">{calcResult}</div>
                <p className="text-xs text-gray-300 mt-4">
                  * Расчёт ориентировочный, не является публичной офертой. Точный КП — после брифа.
                </p>
              </div>
            </div>
          </section>

          <section id="faq">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Частые вопросы</h2>
            <div className="space-y-3">
              <details className="simple-card bg-white/5 backdrop-blur rounded-2xl p-4">
                <summary className="cursor-pointer font-semibold">Что включено в комиссию за подбор?</summary>
                <p className="mt-2 text-sm text-gray-200">Полный цикл — поиск, воронка, интервью, проверка, оффер и гарантия замены.</p>
              </details>
              <details className="simple-card bg-white/5 backdrop-blur rounded-2xl p-4">
                <summary className="cursor-pointer font-semibold">Как считается наценка для временного персонала?</summary>
                <p className="mt-2 text-sm text-gray-200">15% от фактически отработанных часов.</p>
              </details>
              <details className="simple-card bg-white/5 backdrop-blur rounded-2xl p-4">
                <summary className="cursor-pointer font-semibold">Можно ли зафиксировать SLA?</summary>
                <p className="mt-2 text-sm text-gray-200">Да. KPI, штрафы, бонусы и отчётность в договоре.</p>
              </details>
            </div>
          </section>

          <section id="contact" className="py-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Оставить заявку</h2>
            <form onSubmit={handleContactSubmit} className="simple-card bg-white/5 backdrop-blur rounded-2xl p-6 grid gap-4 md:grid-cols-2">
              <input name="name" value={contactForm.name} onChange={handleContactChange} className="rounded-lg px-3 py-2 text-gray-900" placeholder="Имя" required />
              <input name="phone" value={contactForm.phone} onChange={handleContactChange} className="rounded-lg px-3 py-2 text-gray-900" placeholder="Телефон" type="tel" required />
              <input name="email" value={contactForm.email} onChange={handleContactChange} className="rounded-lg px-3 py-2 text-gray-900 md:col-span-2" placeholder="E-mail" type="email" required />
              <textarea name="message" value={contactForm.message} onChange={handleContactChange} className="rounded-lg px-3 py-2 text-gray-900 md:col-span-2" rows={4} placeholder="Кратко опишите задачу" required></textarea>
              <label className="md:col-span-2 flex items-center gap-2 text-sm text-gray-200">
                <input type="checkbox" name="consent" checked={contactForm.consent} onChange={handleContactChange} className="rounded text-blue-500" required />
                Я соглашаюсь с условиями <a href="/privacy" className="underline hover:text-white transition">обработки персональных данных</a>
              </label>
              <button type="submit" className="btn-accent rounded-lg py-2 font-semibold md:col-span-2 bg-yellow-400 text-blue-900 hover:bg-yellow-300 transition" disabled={isSubmitting || !contactForm.consent}>
                {isSubmitting ? 'Отправка...' : 'Отправить'}
              </button>
              {submitStatus === 'success' && <div className="md:col-span-2 text-center text-sm text-green-300">Ваша заявка успешно отправлена!</div>}
              {submitStatus === 'consent-error' && <div className="md:col-span-2 text-center text-sm text-red-400">Пожалуйста, дайте согласие на обработку данных.</div>}
              {submitStatus === 'error' && <div className="md:col-span-2 text-center text-sm text-red-400">Ошибка отправки. Попробуйте позже.</div>}
            </form>
          </section>
        </main>
      </div>
    </>
  );
}